import { AppNoticeBanner } from "@/components/ui/AppNoticeBanner";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { pickPromoAnnouncements } from "@/lib/announcementSelectors";
import { resolveAppLink } from "@/lib/resolveAppLink";
import { mixpanelService } from "@/services/mixpanel.service";
import { useAuth } from "@/lib/AuthContext";
import { usePathname } from "expo-router";
import React, { useCallback, useMemo } from "react";

type Props = {
  userId: string | undefined;
};

/**
 * Hub promo from `kind === 'promo'` announcements.
 * Image: `banner_thumbnail_url` only (never dropdown `thumbnail_url`).
 * Null banner image → text-only. Ignores `is_read`; audience via RPC.
 */
export function PromoCtaBanner({ userId }: Props) {
  const { user } = useAuth();
  const pathname = usePathname();
  const { data: rows = [] } = useAnnouncements({
    userId,
    enabled: Boolean(userId),
  });

  const promo = useMemo(() => pickPromoAnnouncements(rows)[0] ?? null, [rows]);

  const onTrack = useCallback(async () => {
    if (!promo) return;
    const href = resolveAppLink(promo.link).href;
    await mixpanelService.trackAnnouncementClick(
      promo.id,
      promo.title?.trim() ?? "",
      href,
      user,
      pathname,
    );
    await mixpanelService.flush();
  }, [pathname, promo, user]);

  if (!promo) return null;

  const { href } = resolveAppLink(promo.link);
  const bannerImage = promo.banner_thumbnail_url?.trim() || null;

  return (
    <AppNoticeBanner
      tone="promo"
      title={promo.title}
      summary={promo.summary}
      ctaLabel={promo.cta_label?.trim() || "Learn more"}
      href={href || null}
      imageUrl={bannerImage}
      onPress={href ? () => void onTrack() : undefined}
    />
  );
}
